import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Select from '../../../../components/ui/Select';
import { Tooltip } from '../../../../components/ui/Tooltip';
import { UserSkillView, ProficiencyLevel } from '../../../../models/employee';

interface SkillsSectionProps {
  skills: UserSkillView[];
  // The server allows self-edit too, so this is not the same question as the viewer being a manager.
  canEditProficiency: boolean;
  onSkillClick: (skill: UserSkillView) => void;
  onUpdateProficiency: (skillId: number, newProficiency: ProficiencyLevel) => void;
  /**
   * CO-448. FALSE on your own profile: self-endorsement is refused by the server, so the control is
   * shown disabled with a reason rather than hidden — a missing button teaches nothing.
   */
  canEndorse?: boolean;
  /** Ids the current viewer has already endorsed (see `UserSkillView.endorsedByMe`). */
  endorsedSkillIds?: Set<number>;
  endorsingSkillId?: number | null;
  onEndorse?: (skillId: number) => void;
  onWithdrawEndorsement?: (skillId: number) => void;
}

const SkillsSection: React.FC<SkillsSectionProps> = ({
  skills,
  canEditProficiency,
  onSkillClick,
  onUpdateProficiency,
  canEndorse = false,
  endorsedSkillIds,
  endorsingSkillId,
  onEndorse,
  onWithdrawEndorsement,
}) => {
  const { t, i18n } = useTranslation();

  /**
   * 🔴 CO-447 §3.3.5 — the proficiency label is TRANSLATED; the value stays the raw English enum.
   *
   * These options were already `t()`-wrapped, as `t('Awareness')` / `t('Working')` / `t('Strong')`,
   * keyed on the raw English enum member itself. That is precisely why they never translated: no
   * locale file carries a key called `Awareness`, so i18next echoed the key back and every locale
   * rendered the English word. The value must stay raw — it is what `EditUserSkill` receives — so
   * only the label moves to a real key.
   *
   * One shape across every branch of this array, or `npm run typecheck` rejects the file.
   */
  const proficiencyOptions = [
    { value: 'Awareness', label: t('skills.proficiency.awareness') },
    { value: 'Working', label: t('skills.proficiency.working') },
    { value: 'Strong', label: t('skills.proficiency.strong') },
  ];

  const proficiencyLabel = (proficiency?: string) =>
    proficiency ? t(`skills.proficiency.${proficiency.toLowerCase()}`) : t('skills.proficiency.notSet');

  const getProficiencyColor = (proficiency: string) => {
    switch (proficiency) {
      case 'Strong':
        return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800';
      case 'Working':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800';
      case 'Awareness':
        return 'bg-muted text-muted-foreground border-border';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  // Locale-aware; this was hardcoded to 'en-US' and printed English months on every locale.
  const formatDate = (date: Date) =>
    new Date(date).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">{t('Skills')}</h3>
        <p className="text-sm text-muted-foreground">{skills?.length} {skills?.length !== 1 ? t('skills') : t('skill')}</p>
      </div>
      <div className="space-y-3">
        {skills?.map((skill) => {
          const count = skill?.endorsementCount ?? 0;
          const alreadyEndorsed = !!endorsedSkillIds?.has(skill?.id);
          const busy = endorsingSkillId === skill?.id;
          const endorseDisabled = !canEndorse || busy;
          const endorseHint = !canEndorse
            ? t('skills.endorse.tooltipSelf')
            : alreadyEndorsed
              ? t('skills.endorse.tooltipAlready')
              : '';

          return (
            <div
              key={skill?.id}
              className="border border-border rounded-lg bg-card transition-colors hover:border-primary"
              data-testid="skill-row"
            >
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <button type="button" className="flex-1 text-start" onClick={() => onSkillClick(skill)}>
                    <h4 className="text-base font-semibold text-foreground mb-1.5">{skill?.skillName}</h4>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      {skill?.reportedByName && (
                        <span className="flex items-center">
                          <Icon name="User" size={12} className="me-1" />
                          {t('Reported by')} {skill.reportedByName}
                        </span>
                      )}
                      <span className="flex items-center">
                        <Icon name="Calendar" size={12} className="me-1" />
                        {formatDate(skill?.createdOn)}
                      </span>
                    </div>
                  </button>

                  {/* Proficiency Badge/Selector */}
                  <div className="shrink-0" onClick={(e) => e?.stopPropagation()}>
                    {canEditProficiency ? (
                      <Select
                        options={proficiencyOptions}
                        value={skill?.proficiency}
                        onChange={(value) => onUpdateProficiency(skill?.id, value as ProficiencyLevel)}
                        className="w-32"
                      />
                    ) : (
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getProficiencyColor(skill?.proficiency)}`}
                        data-testid="skill-proficiency"
                      >
                        {proficiencyLabel(skill?.proficiency)}
                      </span>
                    )}
                  </div>
                </div>

                {/*
                  CO-448 — endorsement, visible on every skill card. The count was on the server
                  view model from the start but no projection populated it and no client type
                  declared it, so it has read as absent on every surface in the product.
                */}
                <div className="mt-3 flex items-center justify-between gap-3 border-t border-border pt-3">
                  <span className="text-xs text-muted-foreground" data-testid="endorsement-count">
                    {t('skills.endorse.count', { count })}
                  </span>
                  <Tooltip content={endorseHint}>
                    <button
                      type="button"
                      disabled={endorseDisabled}
                      onClick={() =>
                        alreadyEndorsed ? onWithdrawEndorsement?.(skill.id) : onEndorse?.(skill.id)
                      }
                      className={`inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                        alreadyEndorsed
                          ? 'border-primary text-primary'
                          : 'border-border text-muted-foreground hover:border-primary hover:text-primary'
                      }`}
                      data-testid="endorse-button"
                      aria-pressed={alreadyEndorsed}
                    >
                      <Icon name="ThumbsUp" size={13} className="me-1.5" />
                      {alreadyEndorsed ? t('skills.endorse.withdraw') : t('skills.endorse.button')}
                    </button>
                  </Tooltip>
                </div>
              </div>
            </div>
          );
        })}

        {skills?.length === 0 && (
          <div className="text-center py-12 bg-muted rounded-lg border border-border">
            <Icon name="Award" size={48} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-foreground font-medium mb-1">{t('No skills added yet')}</p>
            <p className="text-sm text-muted-foreground">{t('Add skills to track capabilities')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SkillsSection;
